/**
 * Shared formatting helpers for source file metadata (size + timestamps).
 * Used by the Sources file Info dialog and viewer.
 */

import {formatTimestamp as formatTimestampShared} from '../../../../../utils/formatTimestamp';

const PROCESSING_STAGE_LABELS = {
    STORING_RAW: 'Saving',
    QUEUED: 'Queued',
    PDF_EXTRACTING: 'Extracting text',
    AUDIO_TRANSCRIBING: 'Transcribing',
    IMAGE_DESCRIBING: 'Analyzing image',
    TEXT_EXTRACTING: 'Extracting text',
    BUILDING_ORIGINAL_PM: 'Building document',
    LANGUAGE_DETECTING: 'Detecting language',
    TRANSLATING: 'Translating',
    ENRICHING: 'Enriching',
    EMBEDDING: 'Indexing',
    FAILED: 'Failed',
};

const LANGUAGE_NAMES = new Intl.DisplayNames(['en'], {type: 'language'});

export function isPmDocumentNotReadyError(error) {
    const message = String(error?.message || error || '').toLowerCase();
    return message.includes('pm document not ready');
}

/**
 * Normalizes processing fields from a data-item API payload (list row or detail).
 */
export function extractDataItemProcessingFields(item = {}) {
    const record = item?.item || item?.data || item?.result || item;
    return {
        status: record?.status || null,
        meta: record?.meta || null,
        processingStage: record?.meta?.processing?.stage || record?.stage || null,
        processingError: record?.meta?.processing?.errorMessage || null,
    };
}

export function getProcessingProgressPercent(file = {}) {
    const raw = file?.meta?.processing?.progressPercent
        ?? file?.raw?.meta?.processing?.progressPercent;
    const value = Number(raw);
    return Number.isFinite(value) ? Math.max(0, Math.min(100, Math.round(value))) : null;
}

export function isFileActivelyProcessing(file = {}) {
    // Terminal statuses win: a processed/failed item is never "actively processing",
    // even if a stale meta.processing.stage lingers below DONE.
    const status = String(file?.status || file?.raw?.status || '').toLowerCase();
    if (status === 'processed' || status === 'failed') {
        return false;
    }
    const resolved = resolveFileProcessingStatus(file);
    if (resolved?.kind === 'processing') {
        return true;
    }
    const stage = String(file?.processingStage || file?.meta?.processing?.stage || '').toUpperCase();
    if (stage && stage !== 'DONE' && stage !== 'FAILED') {
        return true;
    }
    return status === 'pending' || status === 'processing';
}

export function formatProcessingStageLabel(stage) {
    if (!stage) {
        return null;
    }
    const normalized = String(stage).trim();
    if (!normalized) {
        return null;
    }
    if (PROCESSING_STAGE_LABELS[normalized]) {
        return PROCESSING_STAGE_LABELS[normalized];
    }
    return normalized
        .toLowerCase()
        .split('_')
        .filter(Boolean)
        .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
        .join(' ');
}

/**
 * Resolves display status for a source file list row.
 * Returns null when no secondary status line is needed.
 */
export function resolveFileProcessingStatus(file = {}) {
    const status = String(file?.status || file?.raw?.status || '').toLowerCase();
    const stage = file?.processingStage || file?.meta?.processing?.stage;
    const progressPercent = file?.meta?.processing?.progressPercent
        ?? file?.raw?.meta?.processing?.progressPercent;
    const errorMessage = file?.processingError
        || file?.meta?.processing?.errorMessage
        || file?.raw?.meta?.processing?.errorMessage
        || null;

    const normalizedStage = String(stage || '').toUpperCase();
    const roundedProgress = Number.isFinite(Number(progressPercent))
        ? Math.max(0, Math.min(100, Math.round(Number(progressPercent))))
        : null;

    if (status === 'failed' || normalizedStage === 'FAILED') {
        return {
            kind: 'failed',
            label: 'Failed',
            showSpinner: false,
            errorMessage,
            progressPercent: null,
        };
    }

    if (status === 'processed' || normalizedStage === 'DONE') {
        return {
            kind: 'ready',
            label: 'Ready',
            showSpinner: false,
            errorMessage: null,
            progressPercent: 100,
        };
    }

    if (status === 'pending' || status === 'processing' || (normalizedStage && normalizedStage !== 'DONE')) {
        const baseLabel = formatProcessingStageLabel(stage) || 'Processing';
        const suffix = roundedProgress != null ? ` · ${roundedProgress}%` : '';
        return {
            kind: 'processing',
            label: `${baseLabel}${suffix}`,
            showSpinner: true,
            errorMessage: null,
            progressPercent: roundedProgress,
        };
    }

    const uploadPhase = file?.meta?.upload?.phase || file?.raw?.meta?.upload?.phase;
    if (uploadPhase === 'PROCESSING') {
        const uploadStage = file?.meta?.upload?.stage || file?.raw?.meta?.upload?.stage;
        return {
            kind: 'processing',
            label: formatProcessingStageLabel(uploadStage) || 'Processing',
            showSpinner: true,
            errorMessage: null,
            progressPercent: roundedProgress,
        };
    }

    return null;
}

export function formatFileSize(bytes) {
    const value = Number(bytes);
    if (!Number.isFinite(value) || value <= 0) {
        return null;
    }
    const units = ['B', 'KB', 'MB', 'GB', 'TB'];
    let size = value;
    let unitIndex = 0;
    while (size >= 1024 && unitIndex < units.length - 1) {
        size /= 1024;
        unitIndex += 1;
    }
    const rounded = unitIndex === 0 ? Math.round(size) : Math.round(size * 10) / 10;
    return `${rounded} ${units[unitIndex]}`;
}

const HAS_TIMEZONE_SUFFIX = /(?:[zZ]|[+-]\d{2}:?\d{2})$/;

/**
 * Parses API timestamps from dash-api ({@code LocalDateTime} serialized without a zone).
 * Those values are UTC wall time; bare ISO strings must not be parsed as local time.
 */
export function parseApiDateTime(value) {
    if (!value && value !== 0) {
        return null;
    }
    if (value instanceof Date) {
        return Number.isNaN(value.getTime()) ? null : value;
    }

    const raw = String(value).trim();
    if (!raw) {
        return null;
    }

    const normalized = HAS_TIMEZONE_SUFFIX.test(raw) ? raw : `${raw}Z`;
    const date = new Date(normalized);
    return Number.isNaN(date.getTime()) ? null : date;
}

/**
 * Formats a parsed API timestamp via the shared util. Delegates the zone-less
 * "LocalDateTime is really UTC" quirk to parseApiDateTime above, then hands off
 * a real Date so relative phrasing ("2h ago") / absolute formatting come from
 * the shared formatTimestamp. Returns null (not the shared util's '–' default)
 * for missing/invalid input, since callers render this inline and must not
 * show a literal placeholder string for absent metadata rows.
 */
export function formatTimestamp(value) {
    const date = parseApiDateTime(value);
    return date ? formatTimestampShared(date, null) : null;
}

/**
 * Returns null when the date looks like a bogus encoder default (year < 1970).
 * Many video containers written by FFmpeg/libavformat carry a 1904-01-01 epoch
 * artefact from the QuickTime base date when no real creation time was recorded.
 */
function sanitizePublishDate(value) {
    if (!value) return null;
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return null;
    if (date.getFullYear() < 1970) return null;
    return value;
}

/**
 * Formats a duration in seconds (or fractional seconds) as m:ss or h:mm:ss.
 */
function formatDuration(seconds) {
    const total = Math.round(Number(seconds));
    if (!Number.isFinite(total) || total < 0) return null;
    const h = Math.floor(total / 3600);
    const m = Math.floor((total % 3600) / 60);
    const s = total % 60;
    if (h > 0) {
        return `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
    }
    return `${m}:${String(s).padStart(2, '0')}`;
}

/**
 * Formats a duration given in milliseconds.
 */
function formatDurationMs(ms) {
    const value = Number(ms);
    if (!Number.isFinite(value) || value <= 0) return null;
    return formatDuration(value / 1000);
}

/**
 * Tries to resolve a human-readable language name from a BCP-47 tag.
 * Falls back to the raw tag when Intl.DisplayNames cannot resolve it.
 */
function formatLanguage(tag) {
    if (!tag) return null;
    try {
        const name = LANGUAGE_NAMES.of(tag);
        return name && name !== tag ? name : tag.toUpperCase();
    } catch (_) {
        return String(tag).toUpperCase();
    }
}

const FIRST_DEFINED = (...values) => values.find((value) => value !== undefined && value !== null && value !== '');

function formatFileType(detail = {}) {
    const mime = FIRST_DEFINED(detail?.mimeType, detail?.contentType, detail?.content_type);
    const kind = FIRST_DEFINED(detail?.fileType, detail?.type, detail?.itemType);
    const extension = FIRST_DEFINED(
        detail?.extension,
        detail?.ext,
        (() => {
            const name = FIRST_DEFINED(detail?.name, detail?.fileName, detail?.filename);
            if (typeof name === 'string' && name.includes('.')) {
                return name.split('.').pop();
            }
            return null;
        })(),
    );

    const upperExt = extension ? String(extension).toUpperCase() : null;
    const mimeStr = mime ? String(mime) : null;
    // Avoid redundant "PDF · pdf" when the MIME is just the bare extension.
    if (upperExt && mimeStr && mimeStr.toLowerCase() !== String(extension).toLowerCase()) {
        return `${upperExt} · ${mimeStr}`;
    }
    return FIRST_DEFINED(upperExt, mimeStr, kind ? String(kind) : null) || null;
}

/**
 * Builds the list of metadata rows (label + value) for a source file.
 * Reads from the normalized file detail (viewer) or a list-row `raw` object.
 * Rows with missing values are omitted.
 */
export function buildSourceFileInfoRows(detail = {}) {
    const publishDate = sanitizePublishDate(
        FIRST_DEFINED(detail?.publishDate, detail?.publish_date),
    );
    const uploadedAt = FIRST_DEFINED(detail?.createdAt, detail?.created_at);
    const updatedAt  = FIRST_DEFINED(detail?.updatedAt,  detail?.updated_at);
    const size       = FIRST_DEFINED(detail?.size, detail?.sizeBytes, detail?.fileSize);

    // Media-specific fields — sourced from the top-level mediaMetadata object
    // (propagated from the canonical FILE row) and meta.processing helpers.
    const mm = detail?.mediaMetadata || detail?.meta?.processing?.mediaMetadata || null;
    const durationSec  = mm?.['xmpdm:duration'] ?? null;
    const durationMs   = detail?.meta?.processing?.mediaDurationMs ?? null;
    const width        = mm?.['tiff:imagewidth']  ? Number(mm['tiff:imagewidth'])  : null;
    const height       = mm?.['tiff:imagelength'] ? Number(mm['tiff:imagelength']) : null;
    const sampleRate   = mm?.['xmpdm:audiosamplerate']   ?? null;
    const channelType  = mm?.['xmpdm:audiochanneltype']  ?? null;
    const detectedLang = detail?.meta?.processing?.detectedLanguage ?? null;

    const durationValue = durationSec != null
        ? formatDuration(durationSec)
        : formatDurationMs(durationMs);

    const resolutionValue = (Number.isFinite(width) && width > 0 && Number.isFinite(height) && height > 0)
        ? `${width} × ${height}`
        : null;

    const audioValue = [
        channelType,
        sampleRate ? `${Number(sampleRate).toLocaleString()} Hz` : null,
    ].filter(Boolean).join(', ') || null;

    const rows = [
        {label: 'Type',       value: formatFileType(detail)},
        {label: 'Size',       value: formatFileSize(size)},
        {label: 'Duration',   value: durationValue},
        {label: 'Resolution', value: resolutionValue},
        {label: 'Audio',      value: audioValue},
        {label: 'Language',   value: formatLanguage(detectedLang)},
        {label: 'Recorded',   value: formatTimestamp(publishDate)},
        {label: 'Uploaded',   value: formatTimestamp(uploadedAt)},
        {label: 'Updated',    value: formatTimestamp(updatedAt)},
    ];

    return rows.filter((row) => row.value);
}
